import { getLocalStorage, setLocalStorage, removeLocalStorage } from '../util/storageUtil';

/**
 * A utility to read and write Genova Connect "Remember Me" details in localStorage.
 * The goal is to allow a Genova Connect user to leave Gear and then pick up where they left off
 * once they return.
 */

const STORAGE_KEY = 'pitl_rr';
const EXPIRATION_IN_MINUTES = 30;

/**
 * Compares the stored timestamp against the expiration timeout and returns whether the stored
 * value has expired.
 *
 * @param storedDate The Date when the object was stored or refreshed in localStorage
 */
const isExpired = (storedDate) => {
	const expirationTime = storedDate?.setMinutes(storedDate.getMinutes() + EXPIRATION_IN_MINUTES) || 0;
	return Date.now() - expirationTime > 0;
};

/**
 * Get the stored object from localStorage and deobfuscate back into the original values.
 * If the stored object has expired, it will be removed from storage and 'null' will be returned.
 *
 * @return The stored details, or null if there are no saved details or if the stored details have
 *         expired
 */
const getStoredValue = () => {
	const value = getLocalStorage(STORAGE_KEY);
	if (!value) {
		return null;
	}

	// A missing timestamp will also be treated as expired
	if (isExpired(new Date(value.ts))) {
		clear();
		return null;
	}

	let dateOfBirthString;
	const dob = new Date(value.dts);
	if (dob.getTime()) {
		dateOfBirthString = dob.toISOString().split('T')[0];
	}

	// gearOrderId was stored as {gid: gearOrderId + dateOfBirth timestamp}
	return {
		gearOrderId: value.gid - (value.dts || 0),
		ts: value.ts,
		dateOfBirth: dateOfBirthString
	};
};

/**
 * Store the relevant portions of the gearOrder in localStorage.
 * Apply minor obfuscation so it isn't completely obvious what values are being stored.
 *
 * The stored values:
 * dts: The timestamp of the patient's date of birth in UTC
 * gid: Gear Order Id + date of birth timestamp
 * ts: The last modified timestamp of the stored object
 *
 * @param gearOrder The order from which to store details
 * @return true if the details were stored successfully, false if an error occurred during the save
 */
const storeValue = (gearOrder) => {
	if (!gearOrder?.id) {
		return false;
	}

	/* dateOfBirth is in ISO-8601 format (yyyy-mm-dd).
	 * By passing this format to the Date constructor it will automatically use UTC timezone */
	const dateOfBirthTimeStamp = new Date(gearOrder.patientInfo?.dateOfBirth).getTime() || 0;
	const orderIdPlusDbTS = gearOrder.id + dateOfBirthTimeStamp;

	const objectToStore = {
		gid: orderIdPlusDbTS,
		ts: Date.now(),
		dts: dateOfBirthTimeStamp
	};

	return setLocalStorage(STORAGE_KEY, objectToStore);
};

/**
 * Remove the stored value from localStorage.
 * This call does not care if the storage is already empty.
 */
const clear = () => {
	return removeLocalStorage(STORAGE_KEY);
};

/**
 * Refresh the last modified timestamp value on the stored data.
 *
 * @return true if the stored object was updated, false if there was no object stored or if the
 *         update causes an exception
 */
const refresh = () => {
	const value = getLocalStorage(STORAGE_KEY);
	if (value) {
		return setLocalStorage(STORAGE_KEY, { ...value, ts: Date.now() });
	}
	return false;
};

/**
 * Checks if there are saved PITL details that match the current orderId or URL.
 * PITL details must have a gearOrderId and dateOfBirth to be valid.
 * When we have a gearOrderId, it must match the saved details. When we're on a 'connect' URL,
 * assume the saved order id is a match.
 *
 * @param gearOrderId The order Id part of the router location, or empty if we're relying on the
 *                    window.location object
 * @return true if the saved PITL details match the given order Id or URL, false if they don't
 *         match or if the saved details are missing gearOrderId and dateOfBirth
 */
const isReturningVisit = (gearOrderId) => {
	const previousPitlDetails = getStoredValue();

	if (!previousPitlDetails?.gearOrderId || !previousPitlDetails.dateOfBirth) {
		return false;
	}

	// If we have an order Id, confirm it is the expected value (or the generic 'connect' URL path value)
	if (gearOrderId === 'connect' || Number(gearOrderId) === previousPitlDetails.gearOrderId) {
		return true;
	} else if (gearOrderId) {
		return false;
	}

	if (
		window.location.hash.includes('/connect') ||
		window.location.hash.includes(`/order/${previousPitlDetails.gearOrderId}`)
	) {
		return true;
	}
	return false;
};

const ConnectRememberMe = {
	getStoredValue,
	storeValue,
	clear,
	refresh,
	isReturningVisit
};

export default ConnectRememberMe;
